<template>
    <v-app>
        <driverBar :plate="plate"></driverBar>
        <v-main>
            <v-container>
                <div class="d-flex align-center mb-3">
                    <div>
                        <div class="title">{{ trip ? 'ทริป ' + trip.date + ' – ' + trip.tripEnd : 'งานวันนี้' }}</div>
                        <div v-if="trip" class="body-2 indigo--text">วันที่ {{ trip.dayNo }} จาก {{ trip.days }}</div>
                        <div class="body-2 grey--text text--darken-1">{{ today }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-chip small outlined>{{ doneCount }}/{{ stops.length }} เสร็จ</v-chip>
                    <v-btn icon :loading="loading" :disabled="loading" @click="load">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </div>

                <v-progress-linear v-if="loading" indeterminate color="orange accent-3"></v-progress-linear>

                <v-alert v-if="!loading && !stops.length" type="info" text>
                    วันนี้ไม่มีสินค้าที่ต้องส่งสำหรับรถคันนี้
                </v-alert>

                <!-- Everything today's stops put on the truck, for checking the
                     load at the depot. Collapsed so the stops stay the first
                     thing on screen once the truck has left. -->
                <v-expansion-panels v-if="loadList.length" class="mb-3">
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            <div>
                                <v-icon small class="mr-1">mdi-truck-check-outline</v-icon>
                                สินค้าที่ต้องขึ้นรถวันนี้ · {{ loadCount }} ชิ้น
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div v-for="(p, i) in loadList" :key="i" class="d-flex body-2 py-1">
                                <div class="pr-2">
                                    <!-- Code first: drivers know products by SAP code. -->
                                    <div>
                                        <span v-if="p.code" class="font-weight-bold mr-1">{{ p.code }}</span>
                                        <span v-if="p.name !== p.code">{{ p.name }}</span>
                                    </div>
                                    <div class="caption grey--text text--darken-1">
                                        จุดที่ {{ p.stops.join(', ') }}
                                    </div>
                                </div>
                                <v-spacer></v-spacer>
                                <div class="font-weight-bold text-no-wrap">× {{ p.qty }}</div>
                            </div>
                            <div class="caption grey--text mt-1">ตามข้อมูล ณ วันที่จัดรถ</div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>

                <template v-for="sec in sections">
                    <div v-if="sections.length > 1 || sec.key !== 'plain'" :key="'h' + sec.key"
                         class="subtitle-2 mt-3 mb-1 d-flex align-center" @click="toggle(sec.key)">
                        {{ sec.title }} · {{ sec.stops.length }}
                        <v-icon v-if="sec.collapsed" small class="ml-1">{{ opened[sec.key] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </div>
                    <template v-if="!sec.collapsed || opened[sec.key]">
                        <div v-for="s in sec.stops" :key="s.id">
                            <div v-if="sec.key === 'done' && D.closedDay(s)" class="caption grey--text">เสร็จ {{ D.closedDay(s) }}</div>
                            <stopCard :stop="s" @open="open"></stopCard>
                        </div>
                    </template>
                </template>

                <v-snackbar v-model="showError" color="red" :timeout="5000">{{ error }}</v-snackbar>
            </v-container>
        </v-main>
    </v-app>
</template>
<script>
import driverBar from '@/components/driver/driverBar'
import stopCard from '@/components/driver/stopCard'
import driverService from '@/components/driverService'
import { retryOwedDelivered } from '@/components/sapDelivered'
import D from '@/components/driverDay'

export default {
    components: { driverBar, stopCard },
    data () {
        return { stops: [], plate: '', loading: false, error: '', showError: false, opened: {} }
    },
    computed: {
        today () { return driverService.todayISO() },
        D: () => D,
        // The trip this day belongs to, when the plate is on one (D.tripInfo on
        // each trip stop; they all share it).
        trip () { const s = this.stops.filter(x => x.trip)[0]; return s ? s.trip : null },
        sections () { return D.tripSections(this.stops, this.today) },
        // Closed, delivered or not: a rejected stop is not work left to do.
        doneCount () { return this.stops.filter(s => D.isClosed(s.status)).length },
        loadList () { return D.loadList(this.stops) },
        loadCount () { return this.loadList.reduce((n, p) => n + p.qty, 0) }
    },
    methods: {
        toggle (key) { this.$set(this.opened, key, !this.opened[key]) },
        open (stop) {
            this.$router.push({ name: 'driverStop', params: { id: stop.id } }).catch(() => {})
        },
        async load () {
            // Vuetify's `loading` prop only draws a spinner -- it does not disable
            // the button, so a fast double-tap can fire this twice before the
            // `:disabled` binding re-renders. Guard in-method as well.
            if (this.loading) return
            this.plate = driverService.readChosenPlate()
            if (!this.plate) {
                // No plate chosen yet is not "no work today" -- it means the
                // driver hasn't picked a truck. Hold the view in a loading state
                // while the redirect lands so the "ไม่มีสินค้า" empty-state
                // alert (which claims there is no work) never gets a frame to
                // show first; that alert stays correct for a plate that
                // genuinely has no stops.
                this.loading = true
                try {
                    await this.$router.push({ name: 'driverCar' })
                } catch (e) { /* navigation duplicated/aborted -- fall through */ }
                this.loading = false
                return
            }
            this.loading = true
            try {
                this.stops = await driverService.loadDay(this.plate, this.today)
                // Pays SAP what an earlier finish could not; silent, not awaited.
                retryOwedDelivered(this.$store.getters.webPath)
                // Idempotent: deterministic ids plus merge, and no step fields are
                // written, so this can run on every load without risk.
                await driverService.ensureStops(this.stops)
                // A reload mid-leg must not silently stop reporting position.
                // Re-arming is idempotent: it keeps the existing age clock, so
                // a driver refreshing repeatedly does not force a fix each time.
                const enroute = this.stops.filter(s => s.status === 'enroute')[0]
                if (enroute) driverService.armPing(enroute)
                else driverService.disarmPing()
            } catch (e) {
                if (e && e.code === driverService.NOT_SIGNED_IN) {
                    // Not a load failure -- there is nobody to load a day for.
                    // Sending the driver back to the keypad is the only useful
                    // response; reporting it as "โหลดไม่สำเร็จ" would just send
                    // them tapping refresh forever.
                    //
                    // Release the ping timer on the way out: it is the one exit
                    // path that used to leave the interval and its two listeners
                    // running for a session that no longer exists.
                    driverService.disarmPing()
                    this.$router.push({ name: 'driverPin' }).catch(() => {})
                } else {
                    this.error = 'โหลดงานวันนี้ไม่สำเร็จ กรุณาลองใหม่'
                    this.showError = true
                }
            }
            this.loading = false
        }
    },
    mounted () { this.load() }
}
</script>
